import { useState, useEffect, useRef, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { useShow } from '@/contexts/ShowContext'
import { useUser } from '@/contexts/UserContext'
import { useModal } from '@/contexts/ModalContext'
import { FiChevronDown, FiX, FiDownload, FiImage, FiVideo, FiTrash2, FiCopy, FiCheck, FiAlertCircle, FiClock, FiMoreVertical, FiInfo } from 'react-icons/fi'
import { TbCancel } from 'react-icons/tb'
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaHeart, FaRegHeart, FaCog } from 'react-icons/fa'
import { IoIosRefresh } from 'react-icons/io'
import { LuAudioLines, LuText, LuVideo, LuImage } from 'react-icons/lu'
import dayjs from 'dayjs'
import AiModelEditor, { ModelSelectorDialog, generationMediaDragType } from '@/components/AiModelEditor'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { useWebSocket } from '@/hooks/useWebSocket'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/shadcn/tabs'
import { Input } from '@/components/ui/shadcn/input'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu'
import Button from '@/components/ui/Button'
import Loader from '@/components/loader'
import TaskModal from '@/components/modals/TaskModal'
import ConfirmModal from '@/components/modals/ConfirmModal'
import ImagePreviewModal from '@/components/modals/ImagePreviewModal'
import VideoPreviewModal from '@/components/modals/VideoPreviewModal'
import AudioPreviewModal from '@/components/modals/AudioPreviewModal'
import { toast } from 'sonner'
import { useDragPreview } from '@/components/ui/DragPreview'

// ==============================
// TaskItem (mirrors studio page)
// ==============================
function TaskItem({ task, onClick, onRecallSettings, onTaskUpdated }) {
    const { cancelTask, deleteTask } = useKagamiBridge()
    const { openModal } = useModal()
    const [loading, setLoading] = useState(false)
    const [liveState, setLiveState] = useState(task.state)
    const [liveStatus, setLiveStatus] = useState(task.status)

    const isActive = ['pending', 'in_progress', 'scheduled'].includes(liveState)

    useWebSocket(isActive ? ['task_update','task_completed','task_failed','task_cancelled'] : [], {
        onMessage: (message) => {
            if (message.data?.taskId !== task._id) return
            const update = message.data
            if (update.state) setLiveState(update.state)
            if (update.status) setLiveStatus(update.status)
            if (['completed', 'failed', 'cancelled'].includes(update.state)) {
                onTaskUpdated?.()
            }
        },
    })

    const handleCancel = async () => {
        const confirmed = await openModal(<ConfirmModal title="Cancel Task" message="Are you sure you want to cancel this task?" />)
        if (!confirmed) return
        try {
            setLoading(true)
            await cancelTask(task._id)
            toast.success('Task cancelled', { description: 'Task has been cancelled', duration: 5000, position: 'top-center' })
            onTaskUpdated?.()
        } catch (error) {
            console.error('Error cancelling task:', error)
            toast.error('Failed to cancel task', { description: error.message, duration: 5000, position: 'top-center' })
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        const confirmed = await openModal(<ConfirmModal title="Delete Task" message="Are you sure you want to delete this task?" />)
        if (!confirmed) return
        try {
            setLoading(true)
            await deleteTask(task._id)
            toast.success('Task deleted', { description: 'Task has been deleted', duration: 5000, position: 'top-center' })
            onTaskUpdated?.()
        } catch (error) {
            console.error('Error deleting task:', error)
            toast.error('Failed to delete task', { description: error.message, duration: 5000, position: 'top-center' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={`flex items-center gap-2 select-none cursor-pointer hover:bg-card px-1 py-2 border-b border-b-secondary ${loading ? 'opacity-30 animate-pulse pointer-events-none' : ''}`} onClick={onClick}>
            <div className='flex items-center justify-center w-4 h-4'>
                {liveState === 'cancelled' && <TbCancel className='w-4 h-4 text-gray-600' />}
                {liveState === 'scheduled' && <FiClock className='w-3 h-3 text-gray-300' />}
                {liveState === 'completed' && <FiCheck className='w-3 h-3 text-green-500' />}
                {liveState === 'failed' && <FiAlertCircle className='w-4 h-4 text-red-500' />}
                {(liveState === 'in_progress' || liveState === 'pending') && <FaCog className='w-3 h-3 text-secondary-foreground/80 animate-spin' />}
            </div>
            <span className='text-secondary-foreground text-xs flex-1 flex items-center gap-2 min-w-0'>
                <span className='text-secondary-foreground text-[10px] bg-secondary/60 rounded-sm px-1 py-0.5 truncate'>{task.model?.name}</span>
                <span className='text-secondary-foreground text-[10px] truncate'>{liveStatus}</span>
            </span>
            <span className='text-secondary-foreground/80 text-[10px] shrink-0'>{dayjs(task.createdAt).format('DD/MM HH:mm')}</span>
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <div className='p-1 rounded-sm hover:bg-secondary/60 text-secondary-foreground/60 hover:text-secondary-foreground'>
                        <FiMoreVertical className='w-3 h-3' />
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={onRecallSettings}>
                        <IoIosRefresh className='w-4 h-4' />
                        <span>Recall settings</span>
                    </DropdownMenuItem>
                    {liveState === 'scheduled' && (
                        <DropdownMenuItem onClick={handleCancel}>
                            <TbCancel className='w-4 h-4' />
                            <span>Cancel</span>
                        </DropdownMenuItem>
                    )}
                    {['failed', 'scheduled', 'cancelled'].includes(liveState) && (
                        <DropdownMenuItem onClick={handleDelete}>
                            <FiTrash2 className='w-4 h-4' />
                            <span>Clear</span>
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

function findFirstImageUrl(obj) {
    if (!obj || typeof obj !== 'object') return null
    try {
        const str = JSON.stringify(obj)
        const match = str.match(/https?:\/\/[^\s"]+?\.(jpg|jpeg|png|gif|webp|bmp|svg)/i)
        return match ? match[0] : null
    } catch {
        return null
    }
}

// MIME type for drag-and-drop from generations to canvas
export const GENERATION_DRAG_TYPE = 'application/x-kagami-storyboard-generation'

// ==============================
// Main component
// ==============================
export default function StoryboardGenerations({ preview, onAddToCanvas }) {
    const { show } = useShow()
    const { user } = useUser()
    const { openModal } = useModal()
    const { runTask } = useKagamiBridge()
    const { showDragPreview } = useDragPreview()

    // --- Tab state ---
    const [activeTab, setActiveTab] = useState('model')

    // --- Model state ---
    const [models, setModels] = useState([])
    const [selectedModel, setSelectedModel] = useState(null)
    const [modelData, setModelData] = useState({})
    const [modelsDialogOpen, setModelsDialogOpen] = useState(false)
    const [hasCategoryFilter, setHasCategoryFilter] = useState(false)

    // --- Generations state ---
    const [generations, setGenerations] = useState([])
    const [genTotalPages, setGenTotalPages] = useState(0)
    const [genTotal, setGenTotal] = useState(0)
    const [genCurrentPage, setGenCurrentPage] = useState(1)
    const [genPageSize] = useState(50)
    const [genLiked, setGenLiked] = useState(false)
    const [genFailed, setGenFailed] = useState(true)
    const [genSearch, setGenSearch] = useState('')
    const [genLoading, setGenLoading] = useState(false)
    const [genTypes, setGenTypes] = useState({ image: true, video: true, audio: true })
    const generationScrollRef = useRef(null)

    // --- Tasks state ---
    const [tasks, setTasks] = useState([])
    const [tasksLoading, setTasksLoading] = useState(false)
    const [activeTasks, setActiveTasks] = useState(0)
    const [completedTasks, setCompletedTasks] = useState(0)

    // ==============================
    // FETCH: Models
    // ==============================
    const fetchModels = async () => {
        try {
            const response = await apiFetch('/api/models')
            if (!response.ok) throw new Error('Failed to fetch models')
            const data = await response.json()
            setModels(data)

            const savedModelId = localStorage.getItem('kagami-storyboard-selected-model')
            const savedModel = savedModelId ? data.find(m => m._id === savedModelId) : null
            const initialModel = savedModel || data[0]

            if (initialModel) {
                setSelectedModel(initialModel)
                initModelData(initialModel)
            }
        } catch (error) {
            console.error('Error fetching models:', error)
        }
    }

    const initModelData = (model) => {
        const md = {}
        if (model.inputs?.length) {
            model.inputs.forEach(input => { md[input.prop] = input.default })
        }
        setModelData(md)
    }

    // ==============================
    // FETCH: Generations
    // ==============================
    const fetchGenerations = async () => {
        if (!show?._id) return
        try {
            setGenLoading(true)
            const params = new URLSearchParams()
            params.set('page', genCurrentPage)
            params.set('size', genPageSize)
            params.set('liked', genLiked.toString())
            params.set('failed', genFailed.toString())
            params.set('search', genSearch)
            params.set('types', Object.keys(genTypes).filter(key => genTypes[key]).join(','))

            const response = await apiFetch(`/api/shows/${show._id}/generations?${params.toString()}`)
            if (!response.ok) throw new Error('Failed to fetch generations')
            const payload = await response.json()
            setGenerations(payload.data)
            setGenTotalPages(payload.totalPages)
            setGenTotal(payload.totalGenerations)
            if (generationScrollRef.current) generationScrollRef.current.scrollTop = 0
        } catch (error) {
            console.error('Error fetching generations:', error)
        } finally {
            setGenLoading(false)
        }
    }

    // ==============================
    // FETCH: Tasks
    // ==============================
    const fetchTasks = async () => {
        if (!show?._id) return
        try {
            const response = await apiFetch(`/api/tasks?show=${show._id}`)
            if (!response.ok) throw new Error('Failed to fetch tasks')
            const data = await response.json()
            setTasks(data.tasks)
            setActiveTasks(data.active)
            setCompletedTasks(data.completed)
        } catch (error) {
            console.error('Error fetching tasks:', error)
        }
    }

    // ==============================
    // WebSocket: live task updates
    // ==============================
    useWebSocket(['task_completed', 'task_failed', 'task_cancelled'], {
        onMessage: () => {
            fetchGenerations()
            fetchTasks()
        },
    })

    // ==============================
    // INIT on mount
    // ==============================
    useEffect(() => {
        fetchModels()
        fetchTasks()
        fetchGenerations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Re-fetch generations when filters / page changes
    useEffect(() => {
        fetchGenerations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [genCurrentPage, genLiked, genFailed, genTypes])

    // Debounced search
    useEffect(() => {
        const getData = async () => {
            if (genCurrentPage > 1) {
                setGenCurrentPage(1)
            } else {
                setGenLoading(true)
                await fetchGenerations()
                setGenLoading(false)
            }
        }
        const debounced = setTimeout(() => getData(), 500)
        return () => clearTimeout(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [genSearch])

    // Re-init model data when model changes
    useEffect(() => {
        if (!selectedModel) return
        localStorage.setItem('kagami-storyboard-selected-model', selectedModel._id)
        const md = {}
        if (selectedModel.inputs?.length) {
            selectedModel.inputs.forEach(input => {
                const raw = modelData[input.prop]
                const existingValue = (raw === '' || raw == null) ? undefined : raw
                let value
                if (input.type === 'options' && input.enum && Array.isArray(input.enum)) {
                    value = input.enum.some(e => e.key === existingValue) ? existingValue : input.default
                } else {
                    value = existingValue ?? input.default
                }
                if (value !== undefined) md[input.prop] = value
            })
        } 
        setModelData(md)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModel])

    // ==============================
    // HANDLERS
    // ==============================
    const handleSubmit = async ({ totalGenerations = 1, run_at = null }) => {
        if (!selectedModel || !show?._id) return
        const groupId = crypto.randomUUID()
        for (let i = 0; i < totalGenerations; i++) {
            await runTask({
                modelId: selectedModel._id,
                payload: modelData,
                showId: show._id,
                group: groupId,
                ...(run_at && { run_at })
            })
        }
        await fetchTasks()
    }

    const updateGeneration = async (generationId, data) => {
        if (!show?._id) return
        try {
            const response = await apiFetch(`/api/shows/${show._id}/generations/${generationId}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            })
            if (!response.ok) throw new Error('Failed to update generation')
            const newGeneration = await response.json()
            setGenerations(prev => prev.map(g => g._id === generationId ? newGeneration : g))
        } catch (error) {
            console.error('Error updating generation:', error)
        }
    }

    const handleDownloadAsset = async (asset) => {
        if (!asset?.url) return
        const blob = await fetch(asset.url).then(res => res.blob())
        const mimeType = blob.type
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `asset_${Date.now()}.${mimeType.split('/')[1]}`
        link.click()
        URL.revokeObjectURL(url)
    }

    const recallTaskSettings = (task) => {
        const model = models.find(m => m._id === task.model?._id || m._id === task.model)
        if (model) setSelectedModel(model)
        if (task.request_body?.payload) setModelData(task.request_body.payload)
        setActiveTab('model')
    }

    const handlePreviewGeneration = (generation) => {
        if (!generation?.url) return
        const asset = { url: generation.url, thumbnail_url: generation.url, name: generation.prompt || '', mediaType: generation.type }
        if (generation.type === 'video') {
            openModal(<VideoPreviewModal video={asset} />)
        } else if (generation.type === 'audio') {
            openModal(<AudioPreviewModal audio={asset} />)
        } else if (generation.type === 'image') {
            openModal(<ImagePreviewModal image={asset} />)
        }
    }

    // ==============================
    // Tab drag-to-switch
    // ==============================
    const tabSwitchTimerRef = useRef(null)

    const handleModelTabDragOver = useCallback((e) => {
        if (!e.dataTransfer.types.includes(GENERATION_DRAG_TYPE)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
    }, [])

    const handleModelTabDragEnter = useCallback((e) => {
        if (!e.dataTransfer.types.includes(GENERATION_DRAG_TYPE)) return
        e.preventDefault()
        if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current)
        tabSwitchTimerRef.current = setTimeout(() => {
            setActiveTab('model')
        }, 400)
    }, [])

    const handleModelTabDragLeave = useCallback(() => {
        if (tabSwitchTimerRef.current) {
            clearTimeout(tabSwitchTimerRef.current)
            tabSwitchTimerRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => { if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current) }
    }, [])

    // ==============================
    // RENDER
    // ==============================
    return (
        <div className='w-80 flex flex-col border-l border-l-secondary/60 bg-background/50 shrink-0'>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 overflow-hidden">
                <TabsList className="w-full shrink-0 rounded-none h-10">
                    <TabsTrigger
                        value="model"
                        className="text-xs"
                        onDragOver={handleModelTabDragOver}
                        onDragEnter={handleModelTabDragEnter}
                        onDragLeave={handleModelTabDragLeave}
                    >
                        Model
                    </TabsTrigger>
                    <TabsTrigger value="generations" className="text-xs">Generations</TabsTrigger>
                    <TabsTrigger value="tasks" className="text-xs flex items-center gap-1">
                        Tasks
                        {activeTasks > 0 && <span className='text-[10px] animate-pulse min-w-4 rounded-sm bg-primary px-[2px] py-[1px] flex items-center justify-center text-primary-foreground'>{activeTasks}</span>}
                    </TabsTrigger>
                </TabsList>

                {/* ---- Model tab ---- */}
                <TabsContent value="model" className="flex flex-col flex-1 overflow-hidden">
                    {/* Model selector */}
                    <div className='flex items-center px-3 py-2 '>
                        <div
                            className='text-xs w-full p-2 bg-secondary rounded-md flex gap-2 items-center cursor-pointer hover:bg-secondary/80 truncate'
                            onClick={() => setModelsDialogOpen(true)}
                        >
                            <span className='truncate flex-1'>{selectedModel?.name || 'Select model'}</span>
                            <FiChevronDown className='w-3 h-3 shrink-0' />
                        </div>
                        <ModelSelectorDialog
                            open={modelsDialogOpen}
                            onOpenChange={setModelsDialogOpen}
                            models={models}
                            selectedModel={selectedModel}
                            onSelectModel={setSelectedModel}
                            hasCategoryFilter={hasCategoryFilter}
                        />
                    </div>
                    {/* Model settings */}
                    <div className='flex-1 overflow-y-auto px-3 py-2'>
                        {selectedModel && (
                            <AiModelEditor
                                selectedModel={selectedModel}
                                modelData={modelData}
                                onModelDataChange={setModelData}
                                preview={preview}
                                onPreviewChange={() => {}}
                                onSubmit={handleSubmit}
                            />
                        )}
                    </div>
                </TabsContent>

                {/* ---- Generations tab ---- */}
                <TabsContent value="generations" className="flex flex-col flex-1 gap-2 overflow-hidden relative">
                    <div className='flex flex-col gap-2 px-3 pt-2'>
                        <div className='flex w-full relative'>
                            <Input type='text' placeholder='Search...' className='w-full' value={genSearch} onChange={(e) => setGenSearch(e.target.value)} />
                            {genSearch && <FiX className='w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer' onClick={() => setGenSearch('')} />}
                        </div>
                        <div className='flex items-center gap-x-4 gap-y-1 px-1 flex-wrap'>
                            {Object.keys(genTypes).map((key) => (
                                <div className='flex items-center gap-1.5' key={key}>
                                    <Checkbox id={`sb-filter-${key}`} name={`sb-filter-${key}`} checked={genTypes[key]} onCheckedChange={(checked) => setGenTypes(prev => ({ ...prev, [key]: checked }))} />
                                    <label htmlFor={`sb-filter-${key}`} className='text-[10px] text-secondary-foreground capitalize select-none'>{key}</label>
                                </div>
                            ))}
                            <div className='flex items-center gap-1.5'>
                                <Checkbox id='sb-filterLiked' name='sb-filterLiked' checked={genLiked} onCheckedChange={(checked) => setGenLiked(checked)} />
                                <label htmlFor='sb-filterLiked' className='text-[10px] text-secondary-foreground capitalize select-none'>Liked</label>
                            </div>
                        </div>
                    </div>

                    <div ref={generationScrollRef} className='flex flex-1 flex-col gap-2 overflow-y-auto px-3 pb-2'>
                        {(() => {
                            const groupedGenerations = generations.reduce((acc, generation) => {
                                const groupKey = generation.group || generation._id
                                if (!acc[groupKey]) acc[groupKey] = []
                                acc[groupKey].push(generation)
                                return acc
                            }, {})

                            return Object.entries(groupedGenerations).map(([groupKey, groupGens]) => (
                                <div key={groupKey} className="flex-none flex flex-col relative overflow-hidden group/gen-group">
                                    <div className={`bg-card gap-1 ${groupGens.length > 1 ? 'grid grid-cols-2' : 'flex flex-col'}`}>
                                        {groupGens.map((generation) => (
                                            <div key={generation._id} className="relative flex-none h-auto select-none group/generation">
                                                {generation.url && generation.task?.state === 'completed' &&
                                                    <div
                                                        className='w-full h-auto relative cursor-grab active:cursor-grabbing overflow-hidden min-h-9/16 border rounded-md'
                                                        onClick={() => handlePreviewGeneration(generation)}
                                                        draggable
                                                        onDragStart={(e) => {
                                                            const dragPreviewThumb = generation.thumbnail_url || findFirstImageUrl(generation.task?.request_body?.payload) || null
                                                            const timelineThumb =
                                                                generation.thumbnail_url ||
                                                                (generation.type === 'image' ? generation.url : null)
                                                            showDragPreview(e, { thumbnailUrl: dragPreviewThumb, name: generation.prompt || 'Generation', mediaType: generation.type })
                                                            e.dataTransfer.setData(GENERATION_DRAG_TYPE, JSON.stringify({ url: generation.url, type: generation.type }))
                                                            e.dataTransfer.setData(generationMediaDragType(generation.type), '')
                                                            const timelineItem = { url: generation.url, mediaType: generation.type, name: generation.prompt || 'Generation', thumbnailUrl: timelineThumb }
                                                            e.dataTransfer.setData('application/timeline-asset', JSON.stringify(timelineItem))
                                                            const isAudio = generation.type === 'audio'
                                                            e.dataTransfer.setData(isAudio ? 'application/timeline-asset-audio' : 'application/timeline-asset-visual', '')
                                                            e.dataTransfer.effectAllowed = 'copy'
                                                            e.stopPropagation()
                                                        }}
                                                    >
                                                        {generation.thumbnail_url ?
                                                            <img src={generation.thumbnail_url} alt={generation.prompt || generation._id} loading="lazy" decoding="async" className='object-contain w-full h-auto pointer-events-none' draggable={false} />
                                                            :
                                                            <div className='w-full aspect-video flex items-center justify-center'>
                                                                {generation.type === 'audio' && <LuAudioLines className='w-1/3 h-1/3 text-secondary-foreground/20' />}
                                                                {generation.type==='video' && 
                                                                    // <LuVideo className='w-1/3 h-1/3 text-secondary-foreground/20' />
                                                                    <video src={generation.url} muted className='object-contain w-full h-auto pointer-events-none' draggable={false} /> // temp falback
                                                                }
                                                                {generation.type === 'image' && <LuImage className='w-1/3 h-1/3 text-secondary-foreground/20' />}
                                                            </div>
                                                        }
                                                        <div className='absolute top-0 left-0 w-full h-full bg-background/50 hidden items-center justify-center pointer-events-none group-hover/generation:flex'>
                                                            <div className='absolute bottom-0 right-0 p-1 pointer-events-auto'>
                                                                <div className='flex items-center bg-card rounded-lg border'>
                                                                    <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); handleDownloadAsset(generation) }}>
                                                                        <FiDownload className="w-3 h-3" />
                                                                    </div>
                                                                    <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); recallTaskSettings(generation.task) }}>
                                                                        <IoIosRefresh className="w-3 h-3" />
                                                                    </div>
                                                                    <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); openModal(<TaskModal task={generation.task} />, 'side') }}>
                                                                        <FiInfo className="w-3 h-3" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='absolute top-0 right-0 p-1 pointer-events-auto'>
                                                            <div className='px-1.5 py-0.5 rounded-lg flex items-center justify-center text-red-500 cursor-pointer group/like-button group-hover:bg-card/80 group-hover/generation:border' onClick={(e) => { e.stopPropagation(); updateGeneration(generation._id, { liked: !generation.liked }) }}>
                                                                <FaHeart className={`w-3 h-3 group-hover/generation:w-3 group-hover/generation:h-3 ${generation.liked ? 'block group-hover/like-button:hidden' : 'hidden group-hover/like-button:block'}`} />
                                                                <FaRegHeart className={`w-3 h-3 opacity-0 group-hover/generation:opacity-100 ${generation.liked ? 'hidden group-hover/like-button:block' : 'block group-hover/like-button:hidden'}`} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                                {generation.task?.state === 'failed' && <div className='bg-card rounded-lg flex flex-col gap-1 px-1 pt-6 pb-1 overflow-hidden'>
                                                    <span className='text-destructive-foreground text-[10px]'>Failed to generate</span>
                                                    <span className='text-destructive-foreground text-[10px]'>{generation.task?.status}</span>
                                                    <div className='absolute bottom-0 right-0 p-1 pointer-events-auto hidden group-hover:flex'>
                                                        <div className='flex items-center bg-card rounded-lg border'>
                                                            <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer">
                                                                <FiCopy className="w-3 h-3" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(generation.task?.status) }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>}
                                            </div>
                                        ))}
                                    </div>

                                    {groupGens[0].task?.request_body?.payload?.prompt && <div className='flex group-hover/gen-group:hidden absolute bottom-1 left-1 right-1 pointer-events-none'>
                                        <div className='text-xs text-secondary-foreground/80 py-1 px-2 bg-background/80 rounded-md flex gap-2'>
                                            {groupGens[0].type === 'image' && <FiImage className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                            {groupGens[0].type === 'video' && <FiVideo className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                            {groupGens[0].type === 'audio' && <LuAudioLines className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                            <span className='text-secondary-foreground/80 text-[10px] line-clamp-2'>{groupGens[0].task?.request_body?.payload?.prompt}</span>
                                        </div>
                                    </div>}

                                    {/* <div className='flex group-hover/gen-group:hidden absolute top-1 left-1 px-1.5 py-0.5 gap-1.5 items-center pointer-events-none bg-background/80 rounded-md'>
                                        {groupGens[0].type === 'image' && <FiImage className='w-3 h-3 text-secondary-foreground/80' />}
                                        {groupGens[0].type === 'video' && <FiVideo className='w-3 h-3 text-secondary-foreground/80' />}
                                        {groupGens[0].type === 'audio' && <LuAudioLines className='w-3 h-3 text-secondary-foreground/80' />}
                                        <span className='text-secondary-foreground text-[10px]'>{groupGens[0].model}</span>
                                    </div> */}
                                </div>
                            ))
                        })()}
                    </div>

                    <div className='flex items-center gap-1 px-2 py-1.5 text-xs text-secondary-foreground border-t border-t-secondary/60'>
                        <Button type='secondary' size='xs' icon={<FaAngleDoubleLeft className='w-3 h-3' />} onClick={() => setGenCurrentPage(1)} disabled={genCurrentPage === 1} />
                        <Button type='secondary' size='xs' icon={<FaAngleLeft className='w-3 h-3' />} onClick={() => setGenCurrentPage(Math.max(1, genCurrentPage - 1))} disabled={genCurrentPage === 1} />
                        <span className='text-[10px]'>{genCurrentPage}/{genTotalPages || 1}</span>
                        <Button type='secondary' size='xs' icon={<FaAngleRight className='w-3 h-3' />} onClick={() => setGenCurrentPage(Math.min(genTotalPages, genCurrentPage + 1))} disabled={genCurrentPage === genTotalPages} />
                        <span className='text-[10px] text-secondary-foreground/60 ml-auto'>{genTotal} total</span>
                    </div>
                    {genLoading && <Loader />}
                </TabsContent>

                {/* ---- Tasks tab ---- */}
                <TabsContent value="tasks" className="flex flex-col flex-1 gap-2 overflow-hidden relative">
                    <div className='flex flex-col overflow-y-auto flex-1 relative'>
                        {tasks.filter(Boolean).map((task) => (
                            <TaskItem
                                key={task._id}
                                task={task}
                                onClick={() => openModal(<TaskModal task={task} />, 'side')}
                                onRecallSettings={() => recallTaskSettings(task)}
                                onTaskUpdated={fetchTasks}
                            />
                        ))}
                        {!tasks.length && !tasksLoading && (
                            <div className='flex items-center justify-center py-8'>
                                <span className='text-xs text-secondary-foreground/40'>No tasks yet</span>
                            </div>
                        )}
                    </div>
                    {tasksLoading && <Loader />}
                </TabsContent>
            </Tabs>
        </div>
    )
}
